import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import posthog from 'posthog-js'

interface User {
  id: string
  email: string
  name: string
  role: string
  vendorId?: string
  subscriptionTier?: number
}

interface ImpersonationState {
  active: boolean
  vendorName?: string
  impersonatedBy?: string
  originalUser: User | null
  originalToken: string | null
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  impersonation: ImpersonationState
  setAuth: (user: User, token: string) => void
  updateSubscriptionTier: (tier: number) => void
  startImpersonation: (vendorUser: User, vendorToken: string, vendorName: string) => void
  stopImpersonation: () => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      impersonation: { active: false, originalUser: null, originalToken: null },
      setAuth: (user, token) => {
        localStorage.setItem('authToken', token)
        set({ user, token, isAuthenticated: true })
      },
      updateSubscriptionTier: (tier) => {
        set((state) => ({
          user: state.user ? { ...state.user, subscriptionTier: tier } : null,
        }))
      },
      startImpersonation: (vendorUser, vendorToken, vendorName) => {
        const state = get()
        // Save current admin session before switching
        const originalUser = state.user
        const originalToken = state.token
        localStorage.setItem('authToken', vendorToken)
        set({
          user: vendorUser,
          token: vendorToken,
          isAuthenticated: true,
          impersonation: {
            active: true,
            vendorName,
            impersonatedBy: originalUser?.id,
            originalUser,
            originalToken,
          },
        })
      },
      stopImpersonation: () => {
        const state = get()
        const { originalUser, originalToken } = state.impersonation
        if (originalUser && originalToken) {
          localStorage.setItem('authToken', originalToken)
          set({
            user: originalUser,
            token: originalToken,
            isAuthenticated: true,
            impersonation: { active: false, originalUser: null, originalToken: null },
          })
        } else {
          // Fallback: full logout if original session is lost
          localStorage.removeItem('authToken')
          posthog.reset()
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            impersonation: { active: false, originalUser: null, originalToken: null },
          })
        }
      },
      logout: () => {
        localStorage.removeItem('authToken')
        posthog.reset()
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          impersonation: { active: false, originalUser: null, originalToken: null },
        })
      },
    }),
    {
      name: 'auth-storage',
    }
  )
)

